import { css } from '@emotion/react';
import { InvoiceModel } from '@frontend/api-invoices';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import {
  useCareCreditPatientData,
  useCareCreditLocationProviderStatus,
  formatCareCreditDataForDisplay,
} from '@frontend/payments-care-credit';
import { theme } from '@frontend/theme';
import { Modal, Text, useModalControl } from '@frontend/design-system';
import { useRefundRequest } from '../../../hooks/use-refund-request';
import { RefundError } from '../RefundError';
import { RefundForm } from '../RefundForm';
import { RefundSuccess } from '../RefundSuccess';

type RefundModalProps = ReturnType<typeof useModalControl>['modalProps'] & {
  invoice: InvoiceModel;
};

export const RefundModal = ({ invoice, ...props }: RefundModalProps) => {
  const { t } = useTranslation('payments');

  return (
    <Modal
      {...props}
      minWidth={400}
      css={css`
        padding: ${theme.spacing(3, 0)};
      `}
    >
      {!invoice ? (
        <Text>{t('No invoice selected')}</Text>
      ) : (
        <RefundModalBody invoice={invoice} closeModal={() => props.onClose()} />
      )}
    </Modal>
  );
};

type RefundModalBodyProps = {
  invoice: InvoiceModel;
  closeModal: () => void;
};

function RefundModalBody({ invoice, closeModal }: RefundModalBodyProps) {
  const { error, refund, refundState, requestTimedOut } = useRefundRequest({ invoice, closeModal });

  const processorType = invoice.payment?.processorType;

  const isCareCreditProcessor = processorType === 'CARE_CREDIT_PROCESSOR';

  const { data: person, isLoading: isPersonLoading } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    { locationIds: [invoice?.locationId], personId: invoice?.person?.id },
    { enabled: !!invoice?.person?.id && !!invoice?.locationId }
  );

  const {
    isProviderConnected: isCareCreditEnabled,
    isProviderLoading: isCareCreditLoading,
    merchantNumber,
  } = useCareCreditLocationProviderStatus({
    locationId: invoice?.locationId,
  });

  const {
    careCreditData,
    isAccountNumberLoading: isCareCreditAccountNumberLoading,
    isLoading: isCareCreditDataLoading,
  } = useCareCreditPatientData(
    {
      person: person,
      invoice: invoice,
      merchantNumber,
      locationId: invoice?.locationId,
    },
    {
      enabled: isCareCreditEnabled && isCareCreditProcessor && !!person && !!invoice,
      usePurlFallback: false,
    }
  );

  const { formattedAccountNumber } = formatCareCreditDataForDisplay(careCreditData);

  if (refundState.requested && !error) {
    return (
      <>
        {isCareCreditProcessor ? (
          <RefundSuccess
            cardholderName={invoice.person.name}
            amountInCents={refundState.amount ?? 0}
            last4={formattedAccountNumber}
            isCareCreditRefund={isCareCreditProcessor}
            isLoading={
              isCareCreditAccountNumberLoading || isCareCreditDataLoading || isPersonLoading || isCareCreditLoading
            }
            onOkay={closeModal}
          />
        ) : (
          <RefundSuccess
            cardholderName={invoice.person.name}
            amountInCents={refundState.amount ?? 0}
            last4={invoice.payment?.paymentDetails.lastFour}
            isCareCreditRefund={isCareCreditProcessor}
            onOkay={closeModal}
          />
        )}
      </>
    );
  }

  if (refundState.requested && requestTimedOut) {
    return <RefundError errorMessage={error?.error?.message} onTryAgain={closeModal} />;
  }

  return <RefundForm refundAction={refund} invoice={invoice} payment={invoice.payment} />;
}
